// Shared toolbar controls used by both the slots-heatmap toolbar and the main
// calendar header, so the two stay visually identical:
//
//   • SegmentedToggle — an elevated "pill on a track" switch (2+ segments).
//   • FacetFilter     — a searchable multi-select popover whose trigger
//                       summarises the current selection and accents when the
//                       facet is narrowing the view.
//
// Both come in two sizes: "sm" (h-8, compact toolbars) and "md" (h-9, the
// roomier page header).

import { useMemo } from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ArrowDownAZ, Check, CheckCheck, ChevronDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

const SIZES = {
  sm: {
    track: "h-8",
    segment: "h-7 px-2.5 text-[12px]",
    trigger: "h-8 px-2.5 text-[12px]",
    icon: "size-3.5",
  },
  md: {
    track: "h-9",
    segment: "h-8 px-3 text-sm",
    trigger: "h-9 px-3 text-sm",
    icon: "size-4",
  },
};

// Generic segmented switch. The active segment lifts onto a `card` surface with
// a soft shadow — reads clearly in both light and dark themes. Pass `stretch` to
// have the track fill its container with equal-width segments (used in the
// calendar Display panel so preset rows read as deliberate edge-to-edge controls).
export function SegmentedToggle({ value, segments, onChange, ariaLabel, size = "sm", stretch = false }) {
  const s = SIZES[size] || SIZES.sm;
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={cn(
        "inline-flex items-center rounded-lg bg-muted p-0.5",
        s.track,
        stretch && "flex w-full",
      )}
    >
      {segments.map((segment) => {
        const Icon = segment.icon;
        const active = value === segment.id;
        return (
          <button
            key={segment.id}
            type="button"
            onClick={() => onChange?.(segment.id)}
            title={segment.title}
            aria-pressed={active}
            className={cn(
              "inline-flex items-center justify-center gap-1.5 rounded-md font-medium transition-all",
              s.segment,
              stretch && "flex-1",
              active
                ? "bg-card text-foreground shadow-sm ring-1 ring-border/60"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {Icon ? <Icon className={cn(s.icon, active ? "text-primary" : "")} /> : null}
            {segment.label}
          </button>
        );
      })}
    </div>
  );
}

// Themed chip styling — mirrors a calendar event card (see calendar-event-item's
// themeCardAppt / themeCardSlot). Both consume the CSS vars from
// buildEventColorTheme applied inline on the button. Selected reads as a "booked"
// appointment (solid tint); unselected reads as an "open slot" (dashed ghost,
// dimmed) so the entity's hue stays visible while clearly off. Works in light &
// dark via the paired `dark:` variants.
const THEMED_CHIP_SELECTED =
  "border-(--event-border-light) bg-(--event-bg-light) text-(--event-text-light) shadow-xs " +
  "hover:bg-(--event-bg-hover-light) dark:border-(--event-border-dark) dark:bg-(--event-bg-dark) " +
  "dark:text-(--event-text-dark) dark:hover:bg-(--event-bg-hover-dark)";
const THEMED_CHIP_UNSELECTED =
  "border-dashed border-(--event-border-light) bg-(--event-bg-ghost-light) text-(--event-text-light) opacity-50 dark:opacity-30 " +
  "hover:opacity-100 hover:bg-(--event-bg-ghost-hover-light) dark:border-(--event-border-dark) " +
  "dark:bg-(--event-bg-ghost-dark) dark:text-(--event-text-dark) dark:hover:bg-(--event-bg-ghost-hover-dark)";

// One quick-filter chip. Two looks share one button: the themed variant (doctors)
// renders the whole name as a calendar event card in that entity's colour with no
// checkbox — selection is the solid-vs-ghost fill — while the default variant
// (clinics) is a checkbox + label chip.
function FacetChip({
  opt,
  selected,
  theme,
  onToggle,
  dragRef = null,
  dragStyle = null,
  dragProps = null,
  isDragging = false,
}) {
  // Chips show the compact `quickLabel` (clinic shortName / doctor displayName);
  // the full `name` stays as the hover title.
  const chipLabel = opt.quickLabel || opt.name;
  return (
    <button
      ref={dragRef}
      type="button"
      // Spread the drag props FIRST: dnd-kit's attributes carry their own
      // `aria-pressed` (undefined unless mid-drag), which would otherwise strip the
      // chip's selected state from assistive tech. Ours describes selection, which
      // matters more here than "currently grabbed", so it has to win.
      {...(dragProps || {})}
      onClick={() => onToggle?.(opt.id)}
      aria-pressed={selected}
      title={opt.name}
      style={{ ...(theme || {}), ...(dragStyle || {}) }}
      className={cn(
        "inline-flex h-7 items-center rounded-md border text-[12px] font-medium transition-colors",
        theme
          ? cn(
              "max-w-[12rem] px-2.5 transition-all",
              selected ? THEMED_CHIP_SELECTED : THEMED_CHIP_UNSELECTED,
            )
          : cn(
              "gap-1.5 px-2",
              selected
                ? "border-primary/40 bg-primary/10 text-foreground"
                : "border-border bg-card text-muted-foreground hover:bg-muted hover:text-foreground",
            ),
        isDragging && "z-10 opacity-60",
      )}
    >
      {theme ? null : (
        <span
          className={cn(
            "flex size-3.5 shrink-0 items-center justify-center rounded-[4px] border transition-colors",
            selected
              ? "border-primary bg-primary text-primary-foreground"
              : "border-muted-foreground/40",
          )}
        >
          {selected ? <Check className="size-2.5" /> : null}
        </span>
      )}
      <span className={cn("truncate", theme ? null : "max-w-[10rem]")}>{chipLabel}</span>
    </button>
  );
}

// The whole chip doubles as the drag handle — no separate grip. The sensor's 8px
// activation distance is what keeps a plain click falling through to onToggle.
// (The saved-filter chips put their listeners on a leaf badge instead, but only
// because those chips nest several interactive controls; these do not.)
function SortableFacetChip(props) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: props.opt.id,
  });
  return (
    <FacetChip
      {...props}
      dragRef={setNodeRef}
      dragStyle={{ transform: CSS.Translate.toString(transform), transition }}
      dragProps={{ ...attributes, ...listeners }}
      isDragging={isDragging}
    />
  );
}

// The inline "quick filter" alternative to FacetFilter: the same options laid out
// as a wrapping row of toggle checkboxes so narrowing is a single click with no
// popover. Selection state and the toggle / select-all / clear handlers are shared
// with the combobox form, so switching presentation never loses the current
// selection. `colorTheme(option)` may return the CSS-var theme from
// buildEventColorTheme — when present the option renders as a calendar-item-styled
// pill in that color instead of the default checkbox chip.
//
// Passing `onReorder` makes the chips drag-sortable. The caller owns the order (it
// is a user preference), so this component only reports the drop.
export function QuickFacetRow({
  icon: Icon,
  label,
  options = [],
  selectedIds = [],
  onToggle,
  onSelectAll,
  onClear,
  colorTheme,
  /** (activeId, overId) => void — omit to leave the row non-sortable. */
  onReorder,
  /** Reset to alphabetical order. Only shown alongside onReorder. */
  onSortByName,
}) {
  const selectedSet = useMemo(
    () => new Set((selectedIds || []).map((id) => String(id || "").trim())),
    [selectedIds],
  );
  const optionIds = useMemo(() => options.map((opt) => opt.id), [options]);
  // A plain click must still toggle the chip, so dragging only begins after 8px of
  // pointer travel — same threshold the saved-filters bar uses.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const total = options.length;
  if (total === 0) {
    return null;
  }

  let count = 0;
  for (const opt of options) {
    if (selectedSet.has(String(opt.id || "").trim())) count += 1;
  }
  const allSelected = count === total;
  const noneSelected = count === 0;
  const isNarrowed = count < total;
  const reorderable = typeof onReorder === "function" && total > 1;

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    onReorder(active.id, over.id);
  };

  const chips = options.map((opt) => {
    const chipProps = {
      opt,
      selected: selectedSet.has(String(opt.id || "").trim()),
      theme: colorTheme?.(opt) || null,
      onToggle,
    };
    return reorderable ? (
      <SortableFacetChip key={opt.id} {...chipProps} />
    ) : (
      <FacetChip key={opt.id} {...chipProps} />
    );
  });

  return (
    <div className="flex items-start gap-2">
      {/* Icon-only group label — the text name stays as the tooltip / a11y label. */}
      <span
        title={label}
        aria-label={label}
        className={cn(
          "inline-flex h-7 shrink-0 items-center justify-center text-xs font-semibold",
          isNarrowed ? "text-primary" : "text-muted-foreground",
        )}
      >
        {Icon ? <Icon className="size-4" /> : <span>{label}</span>}
      </span>
      <div className="flex flex-wrap items-center gap-1.5">
        {/* DndContext/SortableContext render no DOM of their own, so the chips stay
            direct children of the wrapping flex row. */}
        {reorderable ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={optionIds} strategy={rectSortingStrategy}>
              {chips}
            </SortableContext>
          </DndContext>
        ) : (
          chips
        )}
        <span aria-hidden className="mx-0.5 h-4 w-px bg-border" />
        <button
          type="button"
          onClick={onSelectAll}
          disabled={allSelected}
          title="Select all"
          aria-label="Select all"
          className="inline-flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
        >
          <CheckCheck className="size-4" />
        </button>
        <button
          type="button"
          onClick={onClear}
          disabled={noneSelected}
          title="Clear all"
          aria-label="Clear all"
          className="inline-flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
        >
          <X className="size-4" />
        </button>
        {reorderable && typeof onSortByName === "function" ? (
          <button
            type="button"
            onClick={onSortByName}
            title="Sort A–Z"
            aria-label="Sort A to Z"
            className="inline-flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <ArrowDownAZ className="size-4" />
          </button>
        ) : null}
      </div>
    </div>
  );
}

// A tiny rounded summary tag shown inside a facet trigger. `dot` is an optional
// CSS color for a leading swatch (e.g. a tenant's hue).
function SummaryTag({ tone = "muted", dot, children }) {
  return (
    <span
      className={cn(
        "inline-flex max-w-[7rem] items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium",
        tone === "primary"
          ? "bg-primary/15 text-primary"
          : "bg-muted-foreground/10 text-muted-foreground",
      )}
    >
      {dot ? (
        <span className="size-1.5 shrink-0 rounded-full" style={{ background: dot }} />
      ) : null}
      <span className="truncate">{children}</span>
    </span>
  );
}

// Small ghost text-button used in the facet footer (Select all / Clear).
function FooterButton({ onClick, disabled, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="rounded px-2 py-1 text-[11px] font-semibold text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
    >
      {children}
    </button>
  );
}

// A searchable faceted multi-select. The trigger summarises the current
// selection (All / None / up-to-two names / "N selected") and turns accented
// when narrowing; the popover hosts a filterable, keyboard-navigable checklist
// with Select all / Clear and a live x/y counter. `dotColor(option)` may return
// a CSS color to render a per-row swatch.
export function FacetFilter({
  icon: Icon,
  label,
  options = [],
  selectedIds = [],
  onToggle,
  onSelectAll,
  onClear,
  dotColor,
  searchPlaceholder,
  align = "start",
  size = "sm",
}) {
  const s = SIZES[size] || SIZES.sm;
  const selectedSet = useMemo(
    () => new Set((selectedIds || []).map((id) => String(id || "").trim())),
    [selectedIds],
  );
  const selectedOptions = useMemo(
    () => options.filter((opt) => selectedSet.has(String(opt.id || "").trim())),
    [options, selectedSet],
  );

  const total = options.length;
  const count = selectedOptions.length;
  const allSelected = total > 0 && count === total;
  const noneSelected = count === 0;
  const isNarrowed = total > 0 && count < total;
  const disabled = total === 0;

  let summary;
  if (disabled) {
    summary = <SummaryTag>none loaded</SummaryTag>;
  } else if (allSelected) {
    summary = <SummaryTag>All</SummaryTag>;
  } else if (noneSelected) {
    summary = <SummaryTag>None</SummaryTag>;
  } else if (count <= 2) {
    summary = selectedOptions.map((opt) => (
      <SummaryTag key={opt.id} tone="primary" dot={dotColor?.(opt)}>
        {opt.name}
      </SummaryTag>
    ));
  } else {
    summary = <SummaryTag tone="primary">{count} selected</SummaryTag>;
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          title={`Filter by ${label.toLowerCase()}`}
          className={cn(
            "inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border font-medium transition-colors",
            s.trigger,
            isNarrowed
              ? "border-primary/40 bg-primary/5 text-foreground"
              : "border-border bg-card text-foreground hover:bg-muted",
            disabled && "cursor-not-allowed opacity-50 hover:bg-card",
          )}
        >
          {Icon ? (
            <Icon
              className={cn(s.icon, isNarrowed ? "text-primary" : "text-muted-foreground")}
            />
          ) : null}
          <span>{label}</span>
          <span
            aria-hidden
            className="mx-0.5 h-4 w-px bg-border @max-[78rem]/toolbar:hidden"
          />
          <span className="flex items-center gap-1 @max-[78rem]/toolbar:hidden">
            {summary}
          </span>
          {/* When hosted in a cramped `@container/toolbar` (the calendar header),
              the verbose summary tags collapse to a compact count so the whole
              toolbar stays on one line. Inert everywhere else — base layout wins. */}
          {isNarrowed ? (
            <span className="hidden rounded bg-primary/15 px-1.5 py-0.5 text-[11px] font-semibold tabular-nums text-primary @max-[78rem]/toolbar:inline-flex">
              {count}
            </span>
          ) : null}
          <ChevronDown className="size-3.5 text-muted-foreground/70" />
        </button>
      </PopoverTrigger>
      <PopoverContent align={align} className="w-[17rem] p-0">
        <Command>
          <CommandInput placeholder={searchPlaceholder || `Search ${label.toLowerCase()}…`} />
          <CommandList>
            <CommandEmpty>No matches.</CommandEmpty>
            <CommandGroup>
              {options.map((opt) => {
                const id = String(opt.id || "").trim();
                const selected = selectedSet.has(id);
                const dot = dotColor?.(opt);
                return (
                  <CommandItem
                    key={opt.id}
                    value={`${opt.name} ${opt.id}`}
                    onSelect={() => onToggle?.(opt.id)}
                    className="gap-2"
                  >
                    <span
                      className={cn(
                        "flex size-4 shrink-0 items-center justify-center rounded border transition-colors",
                        selected
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-muted-foreground/40",
                      )}
                    >
                      {selected ? <Check className="size-3" /> : null}
                    </span>
                    {dot ? (
                      <span
                        className="size-2 shrink-0 rounded-full"
                        style={{ background: dot }}
                      />
                    ) : null}
                    <span className="truncate">{opt.name}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
          <div className="flex items-center justify-between border-t border-border px-2 py-1.5">
            <div className="flex items-center gap-1">
              <FooterButton onClick={onSelectAll} disabled={allSelected}>
                Select all
              </FooterButton>
              <FooterButton onClick={onClear} disabled={noneSelected}>
                Clear
              </FooterButton>
            </div>
            <span className="select-none px-1 text-[11px] tabular-nums text-muted-foreground">
              {count}/{total}
            </span>
          </div>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
